import { ColumnDef } from '@tanstack/react-table'
import { GetPlayerListTableData } from '@/modules/manage-player/type'
import { PlayerEditFormModal } from './CellAction/components/PlayerEditFormModal'
import { PlayerResetPasswordModal } from './CellAction/components/PlayerResetPasswordModal'
import { PlayerDeleteButton } from './CellAction/components/PlayerDeleteButton'
import AssignPlayerTagsModal from './CellAction/components/AssignPlayerTagsModal'
import { PlayerTagsCell } from '@/common/components/PlayerTagsCell'
import { PhoneCell } from '@/common/components/PhoneCell'
import { UsernameCell } from '@/common/components/UsernameCell'
import { BypassLinePlayer } from './CellAction/components/BypassLinePlayer'
import PartnerActiveToggle from './CellAction/components/PartnerActiveToggle'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Link } from 'react-router-dom'
import PlayerBanModal from './CellAction/components/PlayerBanModal'
import PlayerClearLockoutModal from './CellAction/components/PlayerClearLockoutModal'
import { SortingArrows } from '@/common/components/SortingArrows'
import type { PlayerTagResponse } from '@/modules/tags/types'

const formatDateTime = (value?: string) => {
	if (!value) return '-'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return '-'

	return date.toLocaleDateString('th-TH', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	})
}

export const columns: ColumnDef<GetPlayerListTableData>[] = [
	{
		accessorKey: 'username',
		header: 'ข้อมูลผู้เล่น',
		cell: ({ row }) => {
			const { id, username, phone, tags, rank } = row.original
			return (
				<div className="space-y-1">
					<p className="text-xs text-muted-foreground">{id}</p>
					<div className="block">
						<UsernameCell username={username} />
					</div>
					<div className="block">
						<PhoneCell playerId={id} phone={phone} />
					</div>
					<div className="block">
						<PlayerTagsCell tags={tags || []} />
					</div>
					<p className="text-xs text-muted-foreground">
						แรงก์: {rank?.name || '-'}
					</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'mainWallet.balance',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดเงิน
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const balance = row.original.mainWallet?.balance
			return balance != null ? balance.toLocaleString() : '-'
		},
	},
	{
		accessorKey: 'promotionWallet.balance',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดเงินติดเงื่อนไข
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const balance = row.original.promotionWallet?.balance
			return balance != null ? balance.toLocaleString() : '-'
		},
	},
	{
		accessorKey: 'bank.bankProvider',
		header: 'ข้อมูลบัญชี',
		cell: ({ row }) => {
			const { bank, channel } = row.original
			return (
				<div className="space-y-1">
					<p className="text-sm font-medium">{bank?.bankProvider || '-'}</p>
					<p className="text-xs text-muted-foreground">
						{bank?.accountName || '-'} {bank?.accountNumber || ''}
					</p>
					<p className="text-xs text-muted-foreground">
						ช่องทาง: {channel?.name || '-'}
					</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'createdAt',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					สถานะ/เวลา
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			return (
				<div className="space-y-1">
					<Badge variant={row.original.isBanned ? 'destructive' : 'default'}>
						{row.original.isBanned ? 'ถูกแบน' : 'ไม่ถูกแบน'}
					</Badge>
					<p className="text-xs text-muted-foreground">
						สร้างเมื่อ: {formatDateTime(row.original.createdAt)}
					</p>
					<p className="text-xs text-muted-foreground">
						ออนไลน์ล่าสุด: {formatDateTime(row.original.lastOnline)}
					</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'isPartner',
		header: 'พาร์ทเนอร์',
		cell: ({ row }) => {
			const { id, isPartner, partnerAccount, referringPartner } = row.original
			return (
				<div className="space-y-1">
					<Badge variant={isPartner ? 'default' : 'secondary'}>
						{isPartner ? partnerAccount?.nickname : 'ไม่เป็นพาร์ทเนอร์'}
					</Badge>
					<p className="text-xs text-muted-foreground">ผู้แนะนำ:</p>
					{referringPartner ? (
						<Button asChild variant="link" className="h-auto p-0">
							<Link to={`/operator/manage-partner/${referringPartner.id}`}>
								{referringPartner.nickname}
							</Link>
						</Button>
					) : (
						<p className="text-xs text-muted-foreground">-</p>
					)}
					{isPartner && partnerAccount ? (
						<PartnerActiveToggle
							partnerId={partnerAccount.id}
							partnerNickname={partnerAccount.nickname}
							isActive={partnerAccount.isActive}
						/>
					) : (
						<Button asChild variant="outline" size="sm">
							<Link to={`/operator/get-player-list/create-partner/${id}`}>
								เพิ่มพาร์ทเนอร์
							</Link>
						</Button>
					)}
				</div>
			)
		},
	},
	{
		id: 'actions',
		header: 'จัดการ',
		cell: ({ row }) => {
			const { id, phone, tags } = row.original
			return (
				<div className="flex max-w-[360px] flex-wrap gap-1.5">
					<Button asChild variant="outline" size="sm">
						<Link to={`/operator/get-player-list/player-detail/${id}`}>
							ดูข้อมูลผู้เล่น
						</Link>
					</Button>
					<AssignPlayerTagsModal
						playerId={id}
						playerPhone={phone}
						currentTags={tags as unknown as PlayerTagResponse[]}
					/>
					<div className="flex items-center gap-2 rounded-md border px-2 py-1">
						<span className="text-xs text-muted-foreground">Bypass LINE</span>
						<BypassLinePlayer row={row.original} />
					</div>
					<PlayerEditFormModal player={row.original} />
					<PlayerBanModal player={row.original} />
					<PlayerClearLockoutModal player={row.original} />
					<PlayerResetPasswordModal playerId={id} playerUsername={phone} />
					<PlayerDeleteButton playerId={id} playerUsername={phone} />
				</div>
			)
		},
	},
]

export const writeAccessorKeys = ['actions']
